import React, { useState, useRef, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TppRow } from '../../types'

interface RowListItemProps {
    row: TppRow
    onUpdateTitle: (rowId: string, title: string) => Promise<void>
    onDelete: (row: TppRow) => void
    isUpdating: boolean
}

const RowListItem = ({ row, onUpdateTitle, onDelete, isUpdating }: RowListItemProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState(row.title)
    const inputRef = useRef<HTMLInputElement>(null)

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: row._id,
    })
    const dragStyle: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    }

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    const handleStartEdit = () => {
        setEditValue(row.title)
        setIsEditing(true)
    }

    const handleCancelEdit = () => {
        setEditValue(row.title)
        setIsEditing(false)
    }

    const handleSaveEdit = async () => {
        if (editValue.trim() && editValue !== row.title) {
            await onUpdateTitle(row._id, editValue.trim())
        }
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSaveEdit()
        } else if (e.key === 'Escape') {
            handleCancelEdit()
        }
    }

    return (
        <Stack
            ref={setNodeRef}
            style={dragStyle}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            py={1}
            px={2}
            ml={4}
            sx={{
                borderRadius: '8px',
                '&:hover': {
                    bgcolor: 'figma.grayscale.tier6',
                },
            }}
        >
            {isEditing ? (
                <Stack direction="row" alignItems="center" gap={1} flex={1}>
                    <TextField
                        inputRef={inputRef}
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isUpdating}
                        sx={{ flex: 1 }}
                    />
                    <IconButton size="small" onClick={handleSaveEdit} disabled={isUpdating || !editValue.trim()}>
                        {isUpdating ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" color="success" />}
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit} disabled={isUpdating}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ) : (
                <>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <IconButton
                            size="small"
                            {...attributes}
                            {...listeners}
                            sx={{ cursor: 'grab', color: 'figma.grayscale.tier2', touchAction: 'none' }}
                            aria-label="Drag row"
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="14x400" color="figma.grayscale.textHier2">
                            {row.title}
                        </Typography>
                    </Stack>
                    <Stack direction="row" gap={0.5}>
                        <IconButton size="small" onClick={handleStartEdit} sx={{ color: 'figma.grayscale.tier2' }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => onDelete(row)}
                            sx={{ color: 'figma.grayscale.tier2', '&:hover': { color: 'error.main' } }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </>
            )}
        </Stack>
    )
}

export default RowListItem
